import React, { useEffect, useState, useRef } from 'react';
import { authApi, resolveApiUrl, wechatApi } from '../api';
import { useAuth } from '../AuthContext';
import WeChatLogin from './WeChatLogin';

interface Props {
  onClose: () => void;
}

const Profile: React.FC<Props> = ({ onClose }) => {
  const { user, refreshUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [cropOpen, setCropOpen] = useState(false);
  const [cropImageUrl, setCropImageUrl] = useState('');
  const [cropFile, setCropFile] = useState<File | null>(null);
  const [cropZoom, setCropZoom] = useState(1);
  const [wechatConfigured, setWechatConfigured] = useState(false);
  const [wechatBound, setWechatBound] = useState(false);
  const [wechatLoading, setWechatLoading] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const [form, setForm] = useState({
    nickname: user?.nickname || '',
    email: user?.email || '',
    phone: user?.phone || '',
    avatar: user?.avatar || '',
    bio: user?.bio || '',
  });

  useEffect(() => {
    setForm({
      nickname: user?.nickname || '',
      email: user?.email || '',
      phone: user?.phone || '',
      avatar: user?.avatar || '',
      bio: user?.bio || '',
    });
  }, [user?.avatar, user?.bio, user?.email, user?.nickname, user?.phone]);

  const previewAvatar = form.avatar ? resolveApiUrl(form.avatar) : '';

  // 检查微信配置和绑定状态
  useEffect(() => {
    wechatApi.getConfig().then(res => {
      setWechatConfigured(res.data.configured || Object.values(res.data.channels || {}).some((channel: any) => channel.configured));
    }).catch(() => setWechatConfigured(false));
  }, []);

  useEffect(() => {
    // 通过 user 对象判断是否已绑定微信（检查 username 是否以 wx_ 开头作为简单判断）
    // 更好的方式是后端返回 wechat_bound 字段，这里我们通过 API 获取
    if (user) {
      authApi.getMe().then(res => {
        setWechatBound(!!((res.data as any).wechat_bound || (res.data as any).wechat_openid));
      }).catch(() => {});
    }
  }, [user]);

  const handleWechatUnbind = async () => {
    if (!window.confirm('确定要解绑微信吗？')) return;
    setWechatLoading(true);
    try {
      await wechatApi.unbind();
      await refreshUser();
      setWechatBound(false);
      alert('微信解绑成功');
    } catch (err: any) {
      alert('解绑失败: ' + (err.response?.data?.detail || err.message));
    } finally {
      setWechatLoading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await authApi.updateProfile(form);
      await refreshUser();
      alert('保存成功');
    } catch (err: any) {
      alert('保存失败: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handlePickAvatar = () => {
    avatarInputRef.current?.click();
  };

  const closeCropModal = () => {
    if (cropImageUrl) URL.revokeObjectURL(cropImageUrl);
    setCropOpen(false);
    setCropImageUrl('');
    setCropFile(null);
    setCropZoom(1);
  };

  const buildCroppedAvatar = async (file: File, zoom: number): Promise<File> => {
    const objectUrl = URL.createObjectURL(file);
    try {
      const img = new Image();
      img.src = objectUrl;
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('图片加载失败'));
      });

      const cropSize = 512;
      const canvas = document.createElement('canvas');
      canvas.width = cropSize;
      canvas.height = cropSize;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('浏览器不支持图片裁剪');

      // 居中裁剪 + 缩放（覆盖正方形区域）
      const baseScale = Math.max(cropSize / img.width, cropSize / img.height);
      const finalScale = baseScale * zoom;
      const drawW = img.width * finalScale;
      const drawH = img.height * finalScale;
      const dx = (cropSize - drawW) / 2;
      const dy = (cropSize - drawH) / 2;
      ctx.drawImage(img, dx, dy, drawW, drawH);

      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, 'image/jpeg', 0.92)
      );
      if (!blob) throw new Error('裁剪失败');

      const baseName = file.name.replace(/\.[^.]+$/, '') || 'avatar';
      return new File([blob], `${baseName}_cropped.jpg`, { type: 'image/jpeg' });
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  };

  const uploadAvatarFile = async (file: File) => {
    setAvatarUploading(true);
    try {
      const res = await authApi.uploadAvatar(file);
      const avatarUrl = (res.data as any)?.avatar || '';
      setForm(prev => ({ ...prev, avatar: avatarUrl }));
      await refreshUser();
      alert('头像上传成功');
    } catch (err: any) {
      alert('头像上传失败: ' + (err.response?.data?.detail || err.message));
    } finally {
      setAvatarUploading(false);
    }
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 与后端一致的基础校验，减少无效请求
    const allowed = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowed.includes(file.type)) {
      alert('仅支持 JPG/PNG/GIF/WebP 格式');
      e.target.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('图片大小不能超过 5MB');
      e.target.value = '';
      return;
    }

    const url = URL.createObjectURL(file);
    setCropFile(file);
    setCropImageUrl(url);
    setCropZoom(1);
    setCropOpen(true);
    e.target.value = '';
  };

  const confirmCropUpload = async () => {
    if (!cropFile) return;
    try {
      const cropped = await buildCroppedAvatar(cropFile, cropZoom);
      closeCropModal();
      await uploadAvatarFile(cropped);
    } catch (err: any) {
      alert('裁剪失败: ' + (err?.message || '请重试'));
    }
  };

  const mbti = user?.mbti_type ? user.mbti_type.toUpperCase() : '';
  const mbtiUpdatedAt = user?.mbti_updated_at ? new Date(user.mbti_updated_at).toLocaleString('zh-CN') : '';

  return (
    <div className="profile-page">
      <div className="profile-header">
        <div className="profile-header-bg" />
        <div className="profile-header-inner">
          <button className="profile-back" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <h1>个人信息</h1>
          <button className="profile-save" onClick={save} disabled={saving}>
            {saving ? '保存中' : '保存'}
          </button>
        </div>
      </div>

      <div className="profile-body">
        <div className="profile-card">
          <div className="profile-card-title">🧠 MBTI</div>
          {mbti ? (
            <div className="profile-mbti-row">
              <div className="profile-mbti-type">{mbti}</div>
              <div className="profile-mbti-meta">更新时间：{mbtiUpdatedAt || '未知'}</div>
            </div>
          ) : (
            <div className="profile-mbti-empty">暂无 MBTI 结果。请到「MBTI」页完成自测后自动保存。</div>
          )}
        </div>

        {/* 微信绑定 */}
        {wechatConfigured && (
          <div className="profile-card">
            <div className="profile-card-title">💬 微信账号</div>
            {wechatBound ? (
              <div className="profile-wechat-row">
                <div className="profile-wechat-status">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 0 0 .167-.054l1.903-1.114a.864.864 0 0 1 .717-.098 10.16 10.16 0 0 0 2.837.403c.276 0 .543-.027.811-.05a6.093 6.093 0 0 1-.247-1.722c0-3.577 3.206-6.481 7.16-6.481.306 0 .604.027.9.058C16.566 4.713 12.98 2.188 8.691 2.188zM16.086 9.617c-3.37 0-6.107 2.417-6.107 5.4 0 2.983 2.737 5.4 6.107 5.4.67 0 1.312-.097 1.918-.262a.72.72 0 0 1 .596.08l1.346.788a.272.272 0 0 0 .14.045c.133 0 .244-.11.244-.247 0-.06-.024-.12-.04-.178l-.276-1.048a.494.494 0 0 1 .178-.556c1.476-1.12 2.418-2.77 2.418-4.622 0-2.983-2.737-5.4-6.524-5.4z" fill="#07C160"/></svg>
                  <span style={{ color: '#07C160', fontWeight: 600 }}>已绑定微信</span>
                </div>
                <button className="profile-wechat-unbind" onClick={handleWechatUnbind} disabled={wechatLoading}>
                  {wechatLoading ? '处理中...' : '解绑'}
                </button>
              </div>
            ) : (
              <div className="profile-wechat-row">
                <span style={{ color: 'var(--text-tertiary)', fontSize: 13 }}>未绑定微信，绑定后可使用微信快捷登录</span>
                <WeChatLogin purpose="bind" disabled={wechatLoading} onBusyChange={setWechatLoading}
                  onSuccess={() => { setWechatBound(true); void refreshUser(); }} />
              </div>
            )}
          </div>
        )}

        <div className="profile-card">
          <div className="profile-card-title">🖼️ 头像</div>
          <div className="profile-avatar-row">
            <div className="profile-avatar-preview-wrap">
              {previewAvatar ? (
                <img className="profile-avatar-preview" src={previewAvatar} alt="头像预览" />
              ) : (
                <div className="profile-avatar-fallback">
                  {(form.nickname || user?.username || 'U').charAt(0).toUpperCase()}
                </div>
              )}
            </div>
            <div className="profile-avatar-actions">
              <button
                type="button"
                className="profile-avatar-upload"
                onClick={handlePickAvatar}
                disabled={avatarUploading}
              >
                {avatarUploading ? '上传中...' : (previewAvatar ? '更换头像' : '上传头像')}
              </button>
              <div className="profile-avatar-tip">支持 JPG/PNG/GIF/WebP，最大 5MB</div>
            </div>
          </div>
          <input
            ref={avatarInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            onChange={handleAvatarChange}
            style={{ display: 'none' }}
          />
        </div>

        <div className="profile-card">
          <div className="profile-card-title">👤 基本信息</div>
          <div className="profile-field">
            <label>昵称</label>
            <input value={form.nickname} onChange={e => setForm(prev => ({ ...prev, nickname: e.target.value }))} />
          </div>
          <div className="profile-field">
            <label>邮箱</label>
            <input value={form.email} onChange={e => setForm(prev => ({ ...prev, email: e.target.value }))} />
          </div>
          <div className="profile-field">
            <label>手机号</label>
            <input value={form.phone} onChange={e => setForm(prev => ({ ...prev, phone: e.target.value }))} />
          </div>
          <div className="profile-field">
            <label>个人简介</label>
            <textarea value={form.bio} onChange={e => setForm(prev => ({ ...prev, bio: e.target.value }))} rows={4} />
          </div>
        </div>
      </div>

      {cropOpen && (
        <div className="profile-crop-overlay" onClick={(e) => e.target === e.currentTarget && closeCropModal()}>
          <div className="profile-crop-card">
            <div className="profile-crop-head">
              <h3>裁剪头像</h3>
              <button type="button" className="profile-crop-close" onClick={closeCropModal}>✕</button>
            </div>
            <div className="profile-crop-stage">
              {cropImageUrl && (
                <img
                  src={cropImageUrl}
                  alt="裁剪预览"
                  className="profile-crop-image"
                  style={{ transform: `scale(${cropZoom})` }}
                />
              )}
            </div>
            <div className="profile-crop-control">
              <span>缩放</span>
              <input
                type="range"
                min="1"
                max="2.4"
                step="0.01"
                value={cropZoom}
                onChange={(e) => setCropZoom(Number(e.target.value))}
              />
            </div>
            <div className="profile-crop-actions">
              <button type="button" className="profile-crop-cancel" onClick={closeCropModal}>取消</button>
              <button type="button" className="profile-crop-confirm" onClick={confirmCropUpload} disabled={avatarUploading}>
                {avatarUploading ? '上传中...' : '裁剪并上传'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .profile-page { min-height: 100vh; background: var(--bg); display: flex; flex-direction: column; }
        .profile-header { position: relative; overflow: hidden; }
        .profile-header-bg { position: absolute; inset: 0; background: linear-gradient(135deg, #7C3AED 0%, #8B5CF6 50%, #6366F1 100%); }
        .profile-header-bg::after { content: ''; position: absolute; bottom: -20px; left: -10%; right: -10%; height: 50px; background: var(--bg); border-radius: 50% 50% 0 0; }
        .profile-header-inner { position: relative; z-index: 1; display: flex; align-items: center; justify-content: space-between; padding: 16px 20px; padding-top: calc(16px + var(--status-bar-height, 28px)); padding-bottom: 28px; }
        .profile-header-inner h1 { margin: 0; font-size: 20px; font-weight: 700; color: white; }
        .profile-back, .profile-save { height: 36px; border-radius: 18px; border: none; color: white; cursor: pointer; backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px); transition: all 200ms; }
        .profile-back { width: 36px; border-radius: 50%; background: rgba(255,255,255,0.2); display: flex; align-items: center; justify-content: center; }
        .profile-save { padding: 0 14px; background: rgba(255,255,255,0.22); font-weight: 700; }
        .profile-save:disabled { opacity: 0.6; cursor: not-allowed; }
        .profile-back:active, .profile-save:active { transform: scale(0.92); background: rgba(255,255,255,0.3); }

        .profile-body { flex: 1; padding: 0 20px 20px; margin-top: -8px; }
        .profile-card { background: var(--surface); border-radius: var(--radius-lg); padding: 18px; box-shadow: var(--shadow-sm); border: 1px solid var(--border-light); margin-bottom: 14px; }
        .profile-card-title { font-weight: 700; margin-bottom: 10px; color: var(--text-primary); }

        .profile-mbti-row { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; }
        .profile-mbti-type { font-size: 34px; font-weight: 900; color: var(--primary); letter-spacing: 4px; }
        .profile-mbti-meta { font-size: 12px; color: var(--text-tertiary); }
        .profile-mbti-empty { font-size: 13px; color: var(--text-tertiary); line-height: 1.6; }

        .profile-avatar-row { display: flex; align-items: center; gap: 14px; }
        .profile-avatar-preview-wrap {
          width: 72px; height: 72px; border-radius: 50%;
          overflow: hidden;
          border: 2px solid rgba(139,92,246,0.35);
          background: rgba(15,23,42,0.7);
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }
        .profile-avatar-preview { width: 100%; height: 100%; object-fit: cover; display: block; }
        .profile-avatar-fallback {
          width: 100%; height: 100%;
          display: flex; align-items: center; justify-content: center;
          background: linear-gradient(135deg, #8B5CF6, #6366F1);
          color: white; font-size: 24px; font-weight: 800;
        }
        .profile-avatar-actions { flex: 1; min-width: 0; }
        .profile-avatar-upload {
          height: 34px; padding: 0 14px;
          border-radius: 999px;
          border: 1px solid rgba(139,92,246,0.35);
          background: rgba(139,92,246,0.12);
          color: #a78bfa;
          font-size: 13px; font-weight: 700;
          cursor: pointer;
          transition: all 0.2s;
          font-family: inherit;
        }
        .profile-avatar-upload:disabled { opacity: 0.6; cursor: not-allowed; }
        .profile-avatar-upload:active { transform: scale(0.96); }
        .profile-avatar-tip { margin-top: 8px; font-size: 12px; color: var(--text-tertiary); }

        .profile-crop-overlay {
          position: fixed; inset: 0; z-index: 2000;
          background: rgba(2, 6, 23, 0.72);
          backdrop-filter: blur(6px);
          display: flex; align-items: center; justify-content: center;
          padding: 20px;
        }
        .profile-crop-card {
          width: min(420px, 100%);
          background: var(--surface);
          border: 1px solid var(--border-light);
          border-radius: 16px;
          box-shadow: var(--shadow-lg);
          padding: 14px;
        }
        .profile-crop-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
        .profile-crop-head h3 { margin: 0; font-size: 16px; color: var(--text-primary); }
        .profile-crop-close {
          width: 30px; height: 30px; border-radius: 50%;
          border: 1px solid var(--border); background: var(--bg);
          color: var(--text-secondary); cursor: pointer;
        }
        .profile-crop-stage {
          width: 100%;
          aspect-ratio: 1 / 1;
          border-radius: 12px;
          border: 1px solid var(--border);
          background: rgba(15, 23, 42, 0.75);
          overflow: hidden;
          display: flex; align-items: center; justify-content: center;
        }
        .profile-crop-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transform-origin: center center;
          user-select: none;
          pointer-events: none;
        }
        .profile-crop-control { margin-top: 12px; display: flex; align-items: center; gap: 10px; }
        .profile-crop-control span { font-size: 12px; color: var(--text-tertiary); min-width: 32px; }
        .profile-crop-control input[type="range"] { width: 100%; }
        .profile-crop-actions { margin-top: 14px; display: flex; justify-content: flex-end; gap: 10px; }
        .profile-crop-cancel, .profile-crop-confirm {
          height: 34px; padding: 0 14px; border-radius: 999px; border: none; cursor: pointer; font-family: inherit; font-weight: 700;
        }
        .profile-crop-cancel { background: rgba(148,163,184,0.12); color: var(--text-secondary); }
        .profile-crop-confirm { background: rgba(139,92,246,0.18); border: 1px solid rgba(139,92,246,0.4); color: #a78bfa; }
        .profile-crop-confirm:disabled { opacity: 0.6; cursor: not-allowed; }

        .profile-field { margin-bottom: 14px; }
        .profile-field label { display: block; font-size: 12px; font-weight: 700; color: var(--text-tertiary); text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 6px; }
        .profile-field input, .profile-field textarea {
          width: 100%; padding: 12px 14px; border: 1.5px solid var(--border); border-radius: var(--radius-md);
          font-size: 14px; font-family: inherit; color: var(--text-primary); background: var(--bg); box-sizing: border-box;
        }
        .profile-field input:focus, .profile-field textarea:focus { outline: none; border-color: var(--primary); background: var(--surface); }

        .profile-wechat-row { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
        .profile-wechat-status { display: flex; align-items: center; gap: 8px; }
        .profile-wechat-bind, .profile-wechat-unbind {
          padding: 8px 16px; border-radius: 8px; font-size: 13px; font-weight: 600;
          cursor: pointer; border: none; transition: all 200ms; font-family: inherit;
        }
        .profile-wechat-bind {
          background: #07C160; color: white;
        }
        .profile-wechat-bind:hover { background: #06AD56; box-shadow: 0 2px 8px rgba(7,193,96,0.3); }
        .profile-wechat-bind:disabled { opacity: 0.6; cursor: not-allowed; }
        .profile-wechat-unbind {
          background: rgba(239,68,68,0.1); color: #ef4444; border: 1px solid rgba(239,68,68,0.2);
        }
        .profile-wechat-unbind:hover { background: rgba(239,68,68,0.2); }
        .profile-wechat-unbind:disabled { opacity: 0.6; cursor: not-allowed; }

        @media (min-width: 768px) { .profile-body { max-width: 600px; margin-left: auto; margin-right: auto; } }
      `}</style>
    </div>
  );
};

export default Profile;
